import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import axiosWrapper from "../services/axiosWrapper";
import { CONSTANTS } from "@CubeComponents/constants/globalVariable";
import { formCategories } from "@Helpers";
import {
  getPortalUserListAction,
  getPortalUserCountAction,
  setPortalDataAction,
  mergePortalDataAction,
  setPortalEntityListAction,
  setPortalCategoryListAction,
  getCostDatabaseDataAction,
} from "../reducers/portalSetting";
import type {
  GetPortalDataRequest,
  GetPortalDataResponse,
  GetPortalUsedSeatsRequest,
  GetPortalUsedSeatsResponse,
  ChangePortalRolePermissionRequest,
  ChangePortalRolePermissionResponse,
  GetPortalUsersRequest,
  GetPortalUsersResponse,
  AddUserToPortalRequest,
  AddUserToPortalResponse,
  ChangeUserPortalProfileRequest,
  ChangeUserPortalProfileResponse,
  GetPortalEntityListResponse,
  GetFormTemplateRequest,
  GetFormTemplateResponse,
  CustomModuleControllerResponse,
} from "@ServerTypes/portal/portal.schema";
import type {
  CreateCostDatabaseRequest,
  CreateCostDatabaseResponse,
  DeleteCostDatabaseDataRequest,
  GetCostDatabaseDataRequest,
  GetCostDatabaseDataResponse,
  UpdateCostDatabaseRequest,
  UpdateCostDatabaseResponse,
} from "@ServerTypes/costDatabase/costDatabase.schema";
import type { UpdateGroupsDataRequest, UpdateGroupsDataResponse } from "@ServerTypes/groups/groups.schema";
import type {
  ProjectTemplateControllerRequest,
  ProjectTemplateControllerResponse,
  ProjectTemplateSpaceMutationResponse,
  SpaceInProjectTemplateRequest,
} from "@ServerTypes/projectTemplates/projectTemplates.schema";
import type { Exact } from "@Helpers/types/exact";
import type { GetResourceStatusCategoryResponse } from "@ServerTypes/resourceStatus/resourceStatus.schema";
import { resolvePortalURLFrontend } from "@Helpers/localDevelopment";

// Custom module controller request can have different shapes based on action
interface CustomModuleControllerRequest {
  action?: "insert" | "update" | "delete";
  data?: {
    _id?: string;
    name?: string;
    description?: string;
    singularLabel?: string;
    pluralLabel?: string;
    icon?: string;
  };
  moduleId?: string;
  value?: {
    skip?: number;
    take?: number;
    sorted?: Array<{ name: string; direction: string }>;
    where?: unknown[];
    requiresCounts?: boolean;
  };
}

export function setSettingContent(data: Record<string, unknown>) {
  return {
    type: "SET_SETTING_CONTENT",
    data,
  };
}

export function setStatusData(data: Record<string, unknown>) {
  return {
    type: "SET_STATUS_LIST",
    data,
  };
}

const getPortalUserList = getPortalUserListAction;
const getPortalUserCount = getPortalUserCountAction;
const setPortalData = setPortalDataAction;
const mergePortalData = mergePortalDataAction;
const setPortalEntityList = setPortalEntityListAction;
const setPortalCategoryList = setPortalCategoryListAction;
const getCostDatabaseData = getCostDatabaseDataAction;

// Dispatch to the dedicated per-action endpoints (split from the old customModuleController dispatcher).
const CUSTOM_MODULE_ACTION_PATHS: Record<string, string> = {
  insert: "/portal/insertCustomModule",
  update: "/portal/updateCustomModule",
  delete: "/portal/deleteCustomModule",
};

export async function customModuleController(data: CustomModuleControllerRequest) {
  const path = (data.action && CUSTOM_MODULE_ACTION_PATHS[data.action]) || "/portal/customModuleController";
  const responseData = await axiosWrapper.post<CustomModuleControllerResponse>(`${getBackendUrl()}${path}`, data);
  return responseData;
}

export function getPortalData() {
  return async (dispatch: AppDispatch) => {
    const portalFrontendUrl = resolvePortalURLFrontend(window.location.host);
    const requestData: GetPortalDataRequest = {
      value: {
        where: [
          {
            isComplex: false,
            field: "frontendUrl",
            operator: "equal",
            value: portalFrontendUrl,
          },
        ],
      },
    };
    const response = await axiosWrapper.post<GetPortalDataResponse>(
      `${getBackendUrl()}/portal/getPortalData`,
      requestData,
    );
    // `result` rides the success arm only; the failure arm is the central error handler's body.
    const portalRow = response.data.status ? response.data.result[0] : undefined;
    // `portalDataRow` already types the row (`_id` included); the spread is still taken at the open
    // record type the portal slice is merged as, so only the id read is tightened here.
    const portalData: Record<string, unknown> | undefined = portalRow;
    let data = {
      ...portalData,
      logoUrl: `${getUrlForImage({ id: portalRow?._id }, CONSTANTS.PORTAL)}?v=${Date.now()}`,
      signUpLogoUrl: `${getUrlForImage({ id: portalRow?._id }, CONSTANTS.SIGNUP)}?v=${Date.now()}`,
    };
    // `electronAPI` is injected by the desktop shell's preload script and is absent in the browser,
    // hence the optional calls. Named at the read rather than declared globally.
    (
      window as Window & { electronAPI?: { setPortalDataInDesktop?: (d: unknown) => void } }
    ).electronAPI?.setPortalDataInDesktop?.(data);
    dispatch(setPortalData(data));
  };
}

// KNOWN BUG, server side: `/portal/getPortalUsedSeats` answers a single `countDocuments` number —
// every account in the portal — not the per-role `{ roleName, count }` rows the pro/lite split needs
// (portal.mongo.service.ts `getPortalUsedSeats`, and the NOTE on `getPortalUsedSeatsResponse`).
export function getPortalUsedSeats<T extends Exact<GetPortalUsedSeatsRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetPortalUsedSeatsResponse>(
      `${getBackendUrl()}/portal/getPortalUsedSeats`,
      data,
    );

    const mergedData = {
      usedProSeats: 0,
      usedLiteSeats: 0,
    };

    // The old read asserted that number into an array and threw "resultArray.forEach is not a
    // function", so neither counter ever reached the store. With no role on the count it lands in the
    // same bucket the per-role loop used for every non-`PORTAL_GUEST` role; the lite/guest split
    // stays 0 until the endpoint returns the breakdown.
    if (response.data.status) {
      mergedData.usedProSeats = response.data.result;
    }

    dispatch(mergePortalData(mergedData));
  };
}

export async function changePortalRolePermission<T extends Exact<ChangePortalRolePermissionRequest, T>>(
  data: T,
): Promise<ChangePortalRolePermissionResponse> {
  const response = await axiosWrapper.post<ChangePortalRolePermissionResponse>(
    `${getBackendUrl()}/portal/changePortalRolePermission`,
    data,
  );
  // Returns the BODY, like every sibling here. Handing back the whole AxiosResponse made
  // `res.status` the HTTP status — always 200, so a `{ status: false }` refusal read as success.
  return response.data;
}

export function getPortalUserDetails<T extends Exact<GetPortalUsersRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: the thunk is void-returning and every call site reads the list off redux, so
    // returning the promise here would only turn each `dispatch(...)` into a new floating one.
    void axiosWrapper
      .post<GetPortalUsersResponse>(`${getBackendUrl()}/portal/getPortalUsers`, data)
      .then((response) => {
        if (response.data.status) {
          // Both members are `.optional()` on the success arm — an empty page answers with neither —
          // and both reducers accept `undefined`, so they are dispatched as they arrive.
          dispatch(getPortalUserList(response.data.result));
          dispatch(getPortalUserCount(response.data.count));
        }
      });
  };
}

// Deliberately dispatches nothing: `addUserToPortalResponse` is `{ status, errorCode? }` with no
// payload, so there is nothing for a reducer to store.
export function addUserToPortal<T extends Exact<AddUserToPortalRequest, T>>(data: T) {
  return () => axiosWrapper.post<AddUserToPortalResponse>(`${getBackendUrl()}/portal/addUserToPortal`, data);
}

export async function changePortalRole<T extends Exact<ChangeUserPortalProfileRequest, T>>(
  data: T,
): Promise<ChangeUserPortalProfileResponse> {
  // This route uniquely wraps its body — the handler is `return { result: await …Inner(params) }`.
  // That wrap is now DESCRIBED BY the response schema itself (`changeUserPortalProfileResponse` =
  // `{ result: portalNestedWriteResultSchema }`), so re-wrapping it here would count it twice and
  // callers' `response.result.status` would read the outer envelope instead of the nested bag.
  const response = await axiosWrapper.post<ChangeUserPortalProfileResponse>(
    `${getBackendUrl()}/portal/changeUserPortalProfile`,
    data,
  );
  return response.data;
}

export function getAllPortalEntity() {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: void-returning thunk, consumers read the entity list off redux.
    void axiosWrapper
      .get<GetPortalEntityListResponse>(`${getBackendUrl()}/portal/getPortalEntityList`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setPortalEntityList(response.data.result));
        }
      });
  };
}

/**
 * ⚠️ DEAD, BOTH ENDS. No component dispatches this thunk, and the server registers no
 * `/resourceStatus` router at all (packages/server/resourceStatus/ has only a schema and a constants
 * file), so the request would 404 if it were ever made.
 */
export function getAllPortalCategories() {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: void-returning thunk, consumers read the category list off redux.
    void axiosWrapper
      .get<GetResourceStatusCategoryResponse>(`${getBackendUrl()}/resourceStatus/getResourceStatusCategory`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setPortalCategoryList(response.data.result));
        }
      });
  };
}

export function fetchCostDatabaseData<T extends Exact<GetCostDatabaseDataRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: neither call site awaits the dispatch; the cost-database list is read off redux.
    void axiosWrapper
      .post<GetCostDatabaseDataResponse>(`${getBackendUrl()}/costDatabase/getCostDatabaseData`, data)
      .then((res) => {
        if (res.data.status) {
          dispatch(getCostDatabaseData(res.data.result));
        }
      });
  };
}

export const createCostDatabase = async <T extends Exact<CreateCostDatabaseRequest, T>>(
  data: T,
): Promise<{ data: CreateCostDatabaseResponse }> => {
  const response = await axiosWrapper.post<CreateCostDatabaseResponse>(
    `${getBackendUrl()}/costDatabase/createCostDatabase`,
    data,
  );
  return response;
};

export const updateCostDatabase = async <T extends Exact<UpdateCostDatabaseRequest, T>>(
  data: T,
): Promise<{ data: UpdateCostDatabaseResponse }> => {
  const response = await axiosWrapper.post<UpdateCostDatabaseResponse>(
    `${getBackendUrl()}/costDatabase/updateCostDatabase`,
    data,
  );
  return response;
};

// Cost-database DELETE hits the dedicated /deleteCostDatabaseData route; the response is a status envelope
// (status + errorCode), a subset of GetCostDatabaseDataResponse.
// `ids` is typed FROM the route's own request contract rather than as a plain string[]: the key is an
// ARRAY of branded `objectIdString` on `deleteCostDatabaseDataRequest` (always an array, even for one
// id), and a plain string is not assignable to a branded one.
export const deleteCostDb = async (
  ids: DeleteCostDatabaseDataRequest["key"],
): Promise<{ data: GetCostDatabaseDataResponse }> => {
  const data: DeleteCostDatabaseDataRequest = {
    key: ids,
  };
  const response = await axiosWrapper.post<GetCostDatabaseDataResponse>(
    `${getBackendUrl()}/costDatabase/deleteCostDatabaseData`,
    data,
  );
  return response;
};

/** The group write operations, each of which is its own server route. */
export type GroupsWriteAction =
  "insert" | "update" | "remove" | "delete" | "updateRoleOfUsers" | "setRoleOfUsers" | "remove_user";

// Dispatch to the dedicated per-action group endpoints (split from the old updateGroupsData dispatcher).
const GROUPS_ACTION_PATHS: Record<GroupsWriteAction, string> = {
  insert: "/groups/insertGroups",
  update: "/groups/updateGroups",
  remove: "/groups/removeGroups",
  delete: "/groups/deleteGroups",
  updateRoleOfUsers: "/groups/updateRoleOfUsers",
  setRoleOfUsers: "/groups/setRoleOfUsers",
  remove_user: "/groups/removeUsersFromGroups",
};

/**
 * The operation is an ARGUMENT, not a body field: the server dispatches by route path, so a
 * discriminant in the body would be dead weight (and a way for a caller to steer which branch runs).
 * Omit `action` to hit the read/list route.
 */
export const updateGroupsData = async <T extends Exact<UpdateGroupsDataRequest, T>>(
  data: T,
  action?: GroupsWriteAction,
): Promise<{ data: UpdateGroupsDataResponse }> => {
  const path = action ? GROUPS_ACTION_PATHS[action] : "/groups/updateGroupsData";
  const response = await axiosWrapper.post<UpdateGroupsDataResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

export const getProjectFormTemplate = async (): Promise<{ data: GetFormTemplateResponse }> => {
  const data = {
    resourceType: formCategories.projects,
  } satisfies GetFormTemplateRequest;
  const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/portal/getFormTemplate`, data);
  return response;
};

// Each former action-dispatcher is split into dedicated endpoints. These maps route an `action`
// to its endpoint path; an unmapped/absent action falls back to the list endpoint.
const PROJECT_TEMPLATE_ACTION_PATHS: Record<string, string> = {
  insert: "/projectTemplates/saveProjectTemplateData",
  edit: "/projectTemplates/saveProjectTemplateData",
  delete: "/projectTemplates/deleteProjectTemplate",
};
const SPACE_IN_TEMPLATE_ACTION_PATHS: Record<string, string> = {
  insert: "/projectTemplates/insertSpaceInProjectTemplate",
  edit: "/projectTemplates/editSpaceInProjectTemplate",
  delete: "/projectTemplates/deleteSpaceInProjectTemplate",
};

export const projectTemplateData = async <T extends Exact<ProjectTemplateControllerRequest & { action?: string }, T>>(
  data: T,
): Promise<{ data: ProjectTemplateControllerResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && PROJECT_TEMPLATE_ACTION_PATHS[action]) || "/projectTemplates/projectTemplateData";
  const response = await axiosWrapper.post<ProjectTemplateControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

/**
 * Posts one of the three space MUTATIONS (`action` insert / edit / delete), which share
 * `projectTemplateSpaceMutationResponse`. The `|| ` fallback path is the spaces LIST, whose envelope
 * carries `result`/`count` instead of `message` — no caller reaches it (the list is loaded through the
 * Syncfusion DataManager url), so the type states the mutation envelope every call site actually gets.
 */
export const spaceDataInProjectTemplate = async <
  T extends Exact<SpaceInProjectTemplateRequest & { action?: string }, T>,
>(
  data: T,
): Promise<{ data: ProjectTemplateSpaceMutationResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && SPACE_IN_TEMPLATE_ACTION_PATHS[action]) || "/projectTemplates/spacesDataInProjectTemplate";
  const response = await axiosWrapper.post<ProjectTemplateSpaceMutationResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
